/**
 * Central error-reporting helper.
 *
 * Every place that catches a Supabase / network / unexpected error should
 * funnel through this so we get:
 *   1. A user-visible snackbar (no more silent failures)
 *   2. A Sentry event with context (stack, breadcrumbs, user, tags)
 *   3. Console output in dev for fast iteration
 *
 * Sentry calls are wrapped in try/catch + feature-flagged on the DSN env var
 * so the helper degrades to "just a toast" when Sentry isn't configured (e.g.
 * local dev without VITE_SENTRY_DSN, or before the Vercel integration is set
 * up).
 */
import useNotificationStore from '@setup/store/useNotificationStore';

// Lazy import keeps Sentry out of the critical bundle path until enabled.
let sentryModule: typeof import('@sentry/react') | null = null;
const sentryEnabled = () => !!import.meta.env.VITE_SENTRY_DSN;

async function getSentry() {
  if (!sentryEnabled()) return null;
  if (sentryModule) return sentryModule;
  try {
    sentryModule = await import('@sentry/react');
    return sentryModule;
  } catch {
    return null;
  }
}

export interface ReportErrorOptions {
  /** Short human-readable label of the operation (used as toast + Sentry op name) */
  op: string;
  /** The error from Supabase / fetch / wherever */
  err: unknown;
  /** Suppress the toast (still logs + reports to Sentry) — use sparingly */
  silent?: boolean;
  /** Extra structured context attached to the Sentry event */
  context?: Record<string, unknown>;
  /** Sentry tags for grouping/filtering (e.g. { surface: 'class-dialog' }) */
  tags?: Record<string, string>;
}

function extractMessage(err: unknown): string {
  if (!err) return 'Unknown error';
  if (typeof err === 'string') return err;
  if (typeof err === 'object') {
    const e = err as any;
    return e.message ?? e.error?.message ?? e.general ?? JSON.stringify(err).slice(0, 200);
  }
  return String(err);
}

/**
 * Surface an error to the user and to Sentry.
 * Returns the extracted message so callers can also use it inline if needed.
 */
export function reportError({ op, err, silent, context, tags }: ReportErrorOptions): string {
  const msg = extractMessage(err);

  if (!silent) {
    try {
      useNotificationStore.getState().showNotif(`${op} failed: ${msg}`, false);
    } catch {
      // notification store init failure shouldn't take the app down
    }
  }

  // dev-mode console for fast iteration
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.error(`[reportError] ${op}:`, err, context);
  }

  // fire-and-forget Sentry capture
  void (async () => {
    const Sentry = await getSentry();
    if (!Sentry) return;
    try {
      Sentry.withScope((scope) => {
        scope.setTag('op', op);
        if (tags) {
          for (const [k, v] of Object.entries(tags)) scope.setTag(k, v);
        }
        if (context) scope.setContext('details', context);
        // err may be a plain object (PostgrestError) — wrap in Error for stack
        const captureTarget = err instanceof Error ? err : new Error(`${op}: ${msg}`);
        if (!(err instanceof Error) && err && typeof err === 'object') {
          scope.setContext('original_error', err as Record<string, unknown>);
        }
        Sentry.captureException(captureTarget);
      });
    } catch {
      // never let monitoring break the user flow
    }
  })();

  return msg;
}

/**
 * Convenience for the common case: a Supabase response { data, error }.
 * Returns true if the response had an error (and surfaced it), false if OK.
 */
export function reportSupabaseError(
  op: string,
  result: { error?: { message?: string; code?: string; details?: string | null } | null },
  extra?: Omit<ReportErrorOptions, 'op' | 'err'>,
): boolean {
  if (!result.error) return false;
  reportError({
    op,
    err: result.error,
    ...extra,
    context: {
      code: result.error.code,
      details: result.error.details,
      ...(extra?.context ?? {}),
    },
  });
  return true;
}
